import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture } from '@react-three/drei';
import * as THREE from 'three';

const Earth = () => {
  return (
    <mesh>
      <sphereGeometry args={[1.5, 32, 32]} />
      {/* Simple Earth appearance */}
      <meshStandardMaterial color="#4A90D9" roughness={0.6} metalness={0.1} />
    </mesh>
  );
};

const Moon = () => {
  const moonGroupRef = useRef<THREE.Group>(null);
  const moonMap = useTexture('/textures/moon.jpg');

  useFrame((state, delta) => {
    if (moonGroupRef.current) {
      // Rotate the group to orbit the Earth
      moonGroupRef.current.rotation.y += 0.008;
    }
  });

  return (
    <group ref={moonGroupRef}>
      {/* Position moon at radius 4 on x-axis within the rotating group */}
      <mesh position={[4, 0, 0]}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial map={moonMap} roughness={0.9} />
      </mesh>
    </group>
  );
};

const Starfield = () => {
  const starsGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const count = 300;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      // Random position on a large sphere surface
      const r = 20;
      const theta = 2 * Math.PI * Math.random();
      const phi = Math.acos(2 * Math.random() - 1);
      
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geo;
  }, []);

  return (
    <points geometry={starsGeo}>
      <pointsMaterial color="#FFFFFF" size={0.1} sizeAttenuation={true} />
    </points>
  );
};

export const MoonOrbit: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: '100%', height: Number(height) - 40, borderRadius: '8px', overflow: 'hidden' }}>
        <Canvas camera={{ position: [0, 3, 8], fov: 45 }}>
          <ambientLight intensity={0.2} />
          <directionalLight position={[10, 5, 5]} intensity={1.5} />
          <Starfield />
          <Earth />
          <Moon />
          <OrbitControls enableZoom={true} enablePan={false} />
        </Canvas>
      </div>
      <div style={{ marginTop: '12px', fontSize: '13px', color: '#94A3B8', textAlign: 'center' }}>
        The Moon is tidally locked — we always see the same side from Earth.
      </div>
    </div>
  );
};
